/**
 * Unified utils for extracting the most recent offer by party from negotiations.
 * "Most recent" = first round (when sorted descending) that contains an offer from that party.
 * So if the latest round has only Aventine, the most recent Town offer is from the prior round.
 * All values returned are in AV; callers may convert to MV for display when needed.
 */

const hasValidOfferValue = (offer) =>
  offer != null && (offer.Offer === 0 || (offer.Offer != null && offer.Offer !== undefined && offer.Offer !== ''));

/**
 * @param {Array} negotiations
 * @param {string} offeredBy - 'Aventine' or 'Town'
 * @param {boolean} [liveOfferOnly=false] - When true and `offeredBy === 'Aventine'`, only offers with `LiveOffer === 1` match.
 * @returns {{ offer: Object, round: number }|null}
 */
export const getMostRecentOffer = (negotiations, offeredBy, liveOfferOnly = false) => {
  if (!negotiations || !Array.isArray(negotiations)) return null;
  const sorted = [...negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
  for (const round of sorted) {
    if (!round?.offers || !Array.isArray(round.offers)) continue;
    const offer = round.offers.find((o) => o.OfferedBy === offeredBy);
    if (offer && hasValidOfferValue(offer)) {
      if (liveOfferOnly && offeredBy === 'Aventine' && offer.LiveOffer !== 1) continue;
      return { offer, round: round.round ?? 0 };
    }
  }
  return null;
};

/**
 * @param {Array} negotiations
 * @param {string} offeredBy
 * @param {boolean} [liveOfferOnly=false]
 * @returns {number|null} - Offer value in AV, or null
 */
export const getMostRecentOfferValue = (negotiations, offeredBy, liveOfferOnly = false) => {
  const result = getMostRecentOffer(negotiations, offeredBy, liveOfferOnly);
  return result != null ? Number(result.offer.Offer) : null;
};

/**
 * @param {Array} negotiations
 * @param {string} offeredBy
 * @param {boolean} [liveOfferOnly=false]
 * @returns {number|null}
 */
export const getMostRecentOfferRound = (negotiations, offeredBy, liveOfferOnly = false) => {
  const result = getMostRecentOffer(negotiations, offeredBy, liveOfferOnly);
  return result != null ? result.round : null;
};

export const hasRoundMismatch = (negotiations) => {
  if (!negotiations || !Array.isArray(negotiations)) return false;
  const aventineRound = getMostRecentOfferRound(negotiations, 'Aventine');
  const townRound = getMostRecentOfferRound(negotiations, 'Town');
  return aventineRound !== null && townRound !== null && aventineRound !== townRound;
};

export const isAventineOfferFromPriorRound = (negotiations) => {
  const aventineRound = getMostRecentOfferRound(negotiations, 'Aventine');
  const townRound = getMostRecentOfferRound(negotiations, 'Town');
  return aventineRound !== null && townRound !== null && aventineRound < townRound;
};

export const isTownOfferFromPriorRound = (negotiations, townView) => {
  if (townView) {
    const townResult = getMostRecentOffer(negotiations, 'Town', false);
    const liveAventine = getMostRecentOffer(negotiations, 'Aventine', true);
    if (!townResult || !liveAventine) return false;
    return townResult.round < liveAventine.round;
  }
  const aventineRound = getMostRecentOfferRound(negotiations, 'Aventine');
  const townRound = getMostRecentOfferRound(negotiations, 'Town');
  return hasRoundMismatch(negotiations) && aventineRound !== null && townRound !== null && townRound < aventineRound;
};
